// docs/145 KH1: every hero's look for the kit (game/heroKit.ts), kept in one store so React figures, canvas
// painters and the Phaser board all draw a hero the same way. `HeroLookSync` (mounted once, under the
// connection) reads the cache: the hero (its species), its voice (the body's sex: a feminine voice is a woman,
// docs/137 HN5's even-odds default for a hero with no voice row), its worn items and its dye (`hero_dye`,
// docs/142; no row: as drawn), and each worn piece in its own dye (`item_dye`, docs/156; no row: the hero's).
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { useHeroDyes, useHeroVoices, useItemDyes, useMyHeroes, useWorldHeroes, useWorldItems } from '../subscriptions/hooks';
import { useSession } from '../stores/session';
import { CLASS_NAMES } from '../sim/adapt';
import { heroVoiceOf } from '../audio/voices';
import { bakeHero, classLook, lookKey, outfitDyesOf, outfitOf, type HeroLook } from '../../game/heroKit';

interface LooksStore {
  /** By hero id (decimal string). */
  looks: Readonly<Record<string, HeroLook>>;
  set(looks: Record<string, HeroLook>): void;
}

export const useHeroLooks = create<LooksStore>((set) => ({
  looks: {},
  // a hero whose look did not change keeps its object, so its figures don't redraw
  set: (looks) => set((s) => {
    const next: Record<string, HeroLook> = {};
    for (const [k, l] of Object.entries(looks)) { const old = s.looks[k]; next[k] = old && lookKey(old) === lookKey(l) ? old : l; }
    return { looks: next };
  }),
}));

/** A hero's look, if the cache has the hero. */
export const useHeroLook = (heroId: bigint | number | string | undefined): HeroLook | undefined =>
  useHeroLooks((s) => (heroId === undefined ? undefined : s.looks[heroId.toString()]));

/** The same outside React (Phaser, canvas painters). */
export const heroLookNow = (heroId: bigint | number | string): HeroLook | undefined => useHeroLooks.getState().looks[heroId.toString()];

interface HeroRow { id: bigint; species: number }
interface ItemRow { id?: bigint; heroId: bigint; def: number; equippedSlot?: number | null }

/** Every hero's look from the rows (pure, for tests). */
export function looksOf(heroes: readonly HeroRow[], items: readonly ItemRow[], voices: readonly { heroId: bigint; voice: number }[], dyes: readonly { heroId: bigint; dye: number }[], itemDyes: readonly { itemId: bigint; dye: number }[] = []): Record<string, HeroLook> {
  const worn = new Map<string, ItemRow[]>();
  for (const i of items) {
    if (i.equippedSlot === undefined || i.equippedSlot === null) continue;
    const k = i.heroId.toString();
    let l = worn.get(k);
    if (!l) worn.set(k, (l = []));
    l.push(i);
  }
  const voice = new Map(voices.map((v) => [v.heroId.toString(), v]));
  const dye = new Map(dyes.map((d) => [d.heroId.toString(), d.dye]));
  const own = new Map(itemDyes.map((d) => [d.itemId.toString(), d.dye]));
  const out: Record<string, HeroLook> = {};
  for (const h of heroes) {
    const k = h.id.toString();
    const on = worn.get(k) ?? [], hd = dye.get(k) ?? 0;
    const pieces = outfitDyesOf(on, (i) => (i.id === undefined ? undefined : own.get(i.id.toString())), hd);
    out[k] = { species: h.species, sex: heroVoiceOf(h.id, voice.get(k)) === 1 ? 'female' : 'male', dye: hd, outfit: outfitOf(on), ...(Object.keys(pieces).length ? { dyes: pieces } : {}) };
  }
  return out;
}

/**
 * Keeps `useHeroLooks` in step with the cache, and bakes every look as soon as it changes. It subscribes to the
 * World the player is in (its heroes, their items, voices and dyes); the player's own heroes in other Worlds are
 * drawn in their class's plain kit (KH5), their gear not being subscribed.
 */
export function HeroLookSync() {
  const worldId = useSession((s) => s.worldId);
  const heroes = useWorldHeroes(worldId);
  const items = useWorldItems(worldId);
  const voices = useHeroVoices(worldId);
  const dyes = useHeroDyes(worldId);
  const itemDyes = useItemDyes(worldId);
  const mine = useMyHeroes();
  const looks = useMemo(() => {
    const here = looksOf(heroes, items, voices, dyes, itemDyes);
    for (const h of mine) {
      const k = h.id.toString();
      if (!here[k]) here[k] = classLook(CLASS_NAMES[h.class], h.species, heroVoiceOf(h.id) === 1 ? 'female' : 'male');
    }
    return here;
  }, [heroes, items, voices, dyes, itemDyes, mine]);
  useEffect(() => {
    useHeroLooks.getState().set(looks);
    for (const l of Object.values(looks)) void bakeHero(l);
  }, [looks]);
  return null;
}
